import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { AppService } from '../../../services/app.service';

import { HospitalService } from '../../../services/hospital.service';
import { IHospitalRotation, IHospitalStudent } from '../../../models/Hospital';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { SchedulerProConfig } from '@bryntum/schedulerpro';
import { AppDatePipe } from '../../../pipes/date.pipe';
import { Utils } from '../../../core/Utils';
import { ParameterService } from '../../../services/parameter.service';
import { store } from '../store';

const cache: {
    rotations: { [key: string]: IHospitalRotation[] }
} = {
    rotations: {}
};

type ViewType = 'Student' | 'Lecturer';

@Component({
    selector: 'app-hospital-dashboard-rotations',
    templateUrl: './hospital-dashboard-rotations.component.html'
})
export class HospitalDashboardRotationsComponent implements OnInit, OnDestroy {
    constructor(
        private app: AppService,
        private service: HospitalService,
        private parameterService: ParameterService,
        private appDate: AppDatePipe
    ) { }

    @Input() set companyId(value: string) {
        this._companyId = value;
        this.companyIdSubj.next(value);
    }

    @Input() set academicYearId(value: string) {
        this._academicYearId = value;
        this.academicYearIdSubj.next(value);
    }

    readonly schedulerModel = {
        resources: [],
        events: [],
        assignments: []
    };

    readonly schedulerConfig: Partial<SchedulerProConfig> = {
        columns: [
            { text: 'Name', field: 'name', width: 160 },
            { text: 'Surname', field: 'surname', width: 160 }
        ],
        features: {
            cellMenu: false,
            eventDrag: false,
            eventCopyPaste: false,
            eventEdit: false,
            eventMenu: false,
            eventResize: false,
            taskEdit: false,
            dependencies: false,
            scheduleMenu: false,
            eventTooltip: {
                autoHide: false,
                template: data => {
                    const rec: IHospitalRotation = data.eventRecord['originalData'].data;

                    const df = this.appDate.transform(rec.DateFrom);
                    const dt = this.appDate.transform(rec.DateTo);
                    const people = this.view == 'Lecturer'
                        ? [rec.Student.Name + ' ' + rec.Student.Surname]
                        : (rec.Lecturers || []).map(t => t.Name + ' ' + t.Surname);
                    const url = this.getCourseDetailsUrl(rec.SubjectCode);

                    return `<div><small>${rec.SubjectCode}</small></div>`
                        + `<div class="mb-2"><a href="` + url + `" target="_blank">${rec.SubjectName}</a> <i class="fi fi-rs-arrow-circle-right"></i></div>`
                        + `<div>${df} - ${dt}</div>`
                        + `<div class="mb-2">${rec.WeekCount} ${this.app.translate('hospitalDashboard_weeksPostfix')}</div>`
                        + (people.length ? `<div><i class="fi fi-rs-user"></i> ${people.join(', ')}</div>` : '')
                        + (rec.CompanyName ? `<div><i class="fi fi-rs-marker"></i> ${rec.CompanyName}</div>` : '');
                }
            }
        },
        autoHeight: true,
        readOnly: true,
        weekStartDay: 1
    };

    readonly views: ViewType[] = ['Student', 'Lecturer'];
    readonly viewDisplayFn = (option: ViewType) => this.app.translate(`hospitalDashboard_rotations_view${option}`);

    readonly filterOptions: {
        studyYear: number[],
        programName: string[]
    } = {
            studyYear: [],
            programName: []
        };

    readonly filterValues: {
        text?: string,
        studyYear?: number,
        programName?: string,
        guest?: boolean
    } = {};

    view: ViewType = 'Student';

    get companyId(): string {
        return this._companyId;
    }

    get academicYearId(): string {
        return this._academicYearId;
    }

    private _companyId: string;
    private _academicYearId: string;

    private rotations: IHospitalRotation[] = [];
    private prevCompanyId: string;
    private prevAcademicYearId: string;
    private courseDetailsLinkTemplate: string = '';

    private readonly schedulerFilterSubj = new Subject();

    private companyIdSubj = new BehaviorSubject<string>(this.companyId);
    private academicYearIdSubj = new BehaviorSubject<string>(this.academicYearId);
    private argsSub: Subscription;

    private readonly eventColors = ['#669F2A', '#15B79E', '#0BA5EC', '#2970FF', '#6172F3', '#7A5AF8', '#D444F1', '#F63D68', '#EF6820', '#EAAA08'];

    ngOnInit() {
        (this.schedulerConfig.columns as any[]).forEach(t => {
            t.text = this.app.translate(`hospitalDashboard_rotations_lbl${t.text}`);
        });

        this.schedulerFilterSubj.pipe(debounceTime(300)).subscribe(() => {
            this.buildScheduler(this.rotations);
        });

        this.argsSub = combineLatest([
            this.companyIdSubj.pipe(filter(t => !!t)),
            this.academicYearIdSubj.pipe(filter(t => !!t))
        ]).subscribe(args => {
            const reload = this.companyId != this.prevCompanyId || this.academicYearId != this.prevAcademicYearId;

            this.prevCompanyId = this.companyId;
            this.prevAcademicYearId = this.academicYearId;

            this.init(reload);
        });

        this.parameterService.getValues().subscribe(data => {
            this.courseDetailsLinkTemplate = this.parameterService.findValue(data, 'CourseDetailsUrl', t => t, '');
        });
    }

    ngOnDestroy() {
        if (this.argsSub) {
            this.argsSub.unsubscribe();
        }
    }

    filter() {
        this.schedulerFilterSubj.next();
    }

    changeView(view: ViewType) {
        this.view = view;
        this.buildScheduler(this.rotations);
    }

    getCourseDetailsUrl(code: string) {
        return this.courseDetailsLinkTemplate.replace('{SubjectCode}', code);
    }

    private init(reload?: boolean) {
        const process = (data: IHospitalRotation[]) => {
            this.rotations = data;

            const studyYears: number[] = [];
            const programNames: string[] = [];

            data.forEach(t => {
                if (!studyYears.includes(t.Student.StudyYear)) studyYears.push(t.Student.StudyYear);
                if (!programNames.includes(t.Student.ProgramName)) programNames.push(t.Student.ProgramName);
            });

            studyYears.sort();
            programNames.sort();

            this.filterOptions.studyYear = studyYears;
            this.filterOptions.programName = programNames;

            this.buildScheduler(data);
        };

        const cacheKey = `${this.companyId}_${this.academicYearId}`;

        if (cache.rotations[cacheKey]) {
            process(cache.rotations[cacheKey]);
        } else if (reload) {
            this.app.addLoading(this.service.getRotations(this.companyId, this.academicYearId)).subscribe(data => {
                cache.rotations[cacheKey] = data;
                process(data);
            });
        }
    }

    private buildScheduler(data: IHospitalRotation[]) {
        const filter = this.filterValues;
        let students: IHospitalStudent[] = [];

        const apply = () => {
            let minDate: Date;
            let maxDate: Date;

            const events = [];
            const assignments = [];
            const resources = [];

            let currentColorIndex = 0;

            const noValue = this.app.translate('doctorDashboard_rotations_undefined');

            const applyFilter = (item: IHospitalRotation) => {
                if (filter) {
                    if (filter.studyYear && item.Student.StudyYear != filter.studyYear) return false;
                    if (filter.programName && item.Student.ProgramName != filter.programName) return false;
                    if (filter.guest) {
                        const student = students.find(n => n.Id == item.Student.Id);
                        if (student?.ClinicalBaseId == this.companyId || student?.WorkplaceId == this.companyId) return false;
                    }
                    if (filter.text) {
                        const term = filter.text.toLowerCase();

                        if (!`${item.SubjectCode} ${item.SubjectName}`.toLowerCase().includes(term)
                            && !`${item.Student.Name} ${item.Student.Surname}`.toLowerCase().includes(term)
                            && !`${item.Student.ProgramCode} ${item.Student.ProgramName}`.toLowerCase().includes(term)
                            && !item.Lecturers.some(t => `${t.Name} ${t.Surname}`.toLowerCase().includes(term))
                        ) return false;
                    }
                }

                return true;
            };

            data.forEach((t, i) => {
                const subjectName = `${t.SubjectCode} ${t.SubjectName}`;
                const studentName = `${t.Student.Name} ${t.Student.Surname}`;

                if (applyFilter(t)) {
                    const bd = Utils.ensureDate(t.DateFrom);
                    const ad = Utils.ensureDate(t.DateTo);

                    if (!minDate || bd < minDate) {
                        minDate = bd;
                    }

                    if (!maxDate || ad > maxDate) {
                        maxDate = ad;
                    }

                    const eventId = t.Id + '_' + i;

                    events.push({
                        id: eventId,
                        startDate: bd,
                        endDate: ad,
                        name: subjectName,
                        data: t,
                        eventColor: this.eventColors[currentColorIndex]
                    });

                    if (this.view == 'Lecturer') {
                        const lecturers = t.Lecturers || [];

                        if (!lecturers.length) {
                            lecturers.push({ Id: '', Name: noValue, Surname: noValue });
                        }

                        lecturers.forEach(l => {
                            if (!resources.some(n => n.id == l.Id)) {
                                resources.push({
                                    id: l.Id,
                                    name: l.Name,
                                    surname: l.Surname,
                                    fullName: `${l.Name} ${l.Surname}`
                                });
                            }

                            assignments.push({
                                event: eventId,
                                resource: l.Id
                            });
                        });
                    } else {
                        if (!resources.some(n => n.id == t.Student.Id)) {
                            resources.push({
                                id: t.Student.Id,
                                name: t.Student.Name,
                                surname: t.Student.Surname,
                                fullName: studentName
                            });
                        }

                        assignments.push({
                            event: eventId,
                            resource: t.Student.Id
                        });
                    }

                    currentColorIndex++;

                    if (currentColorIndex == this.eventColors.length) {
                        currentColorIndex = 0;
                    }
                }
            });

            this.schedulerConfig.startDate = minDate;
            this.schedulerConfig.endDate = maxDate;

            this.schedulerModel.resources = resources;
            this.schedulerModel.assignments = assignments;
            this.schedulerModel.events = events;
        };

        if (filter.guest) {
            store.loadStudents(this.service, this.companyId).subscribe(data => {
                students = data;
                apply();
            });
        } else {
            apply();
        }
    }
}
